import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2, Mail } from 'lucide-react';
import { portal } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import GoogleButton from './GoogleButton';
import { stashFreshKey } from '../lib/freshKey';

/**
 * Sign in or create an account, without leaving the page.
 *
 * Opened by useAuth().requireAuth() — from the marketplace when somebody asks
 * to see a listing's details, and from the navbar. Sending them to /dashboard
 * instead would lose the thing they were looking at, which is the moment they
 * were most interested.
 *
 * Google is offered first because it is one tap and needs no password rules
 * explained; email is underneath for anyone who prefers it.
 */
export default function AuthModal({ open, reason, onClose }) {
  const { setSession } = useAuth();
  const [mode, setMode] = useState('signup');   // signup | login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setErr('');
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  // A response that minted a key parks it for <NewApiKeyGate/> to reveal. It
  // cannot be shown from in here: setSession closes this modal in the same
  // tick, so anything held in local state unmounts before it paints.
  const done = useCallback((r) => {
    stashFreshKey(r?.api_key);
    onClose?.(true);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = mode === 'signup'
        ? await portal.signup(email.trim(), password, name.trim())
        : await portal.login(email.trim(), password);
      setSession(r.user, r.portal_key);
      done(r);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const input =
    'w-full bg-[var(--bg)] border border-[var(--border-2)] rounded-sm px-4 py-3 text-[0.95rem] text-[var(--text)] placeholder:text-[var(--text-4)] focus:outline-none focus:border-[var(--accent)] transition-colors';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="auth-modal"
    >
      <div className="w-full max-w-[440px] bg-[var(--bg-2)] border border-[var(--border-2)] rounded-sm p-8 relative">
        <button
          onClick={() => onClose?.()}
          aria-label="Close"
          className="absolute top-4 right-4 text-[var(--text-4)] hover:text-[var(--text)] transition-colors"
        >
          <X size={18} />
        </button>

        <h2 className="serif text-[1.7rem] leading-tight pr-6">
          {mode === 'signup' ? 'Create your account' : 'Welcome back'}
        </h2>
        <p className="text-[0.92rem] text-[var(--text-3)] mt-2 leading-relaxed">
          {reason ||
            (mode === 'signup'
              ? 'Free to join. Two skill generations a month, and the whole marketplace to browse.'
              : 'Sign in to pick up where you left off.')}
        </p>

        <div className="mt-6">
          <GoogleButton
            onDone={done}
            onError={setErr}
            text={mode === 'signup' ? 'signup_with' : 'signin_with'}
          />
        </div>

        <div className="flex items-center gap-4 my-6">
          <span className="h-px flex-1 bg-[var(--border-2)]" />
          <span className="text-[0.75rem] uppercase tracking-wider text-[var(--text-4)]">or</span>
          <span className="h-px flex-1 bg-[var(--border-2)]" />
        </div>

        <form onSubmit={submit} className="grid gap-3">
          {mode === 'signup' && (
            <input
              className={input}
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          )}
          <input
            className={input}
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <input
            className={input}
            type="password"
            required
            placeholder={mode === 'signup' ? 'Password (12 characters or more)' : 'Password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
          {err && (
            <div className="text-[0.88rem] text-[var(--danger)] leading-snug" data-testid="auth-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary !py-3 justify-center mt-1">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            {mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div className="text-[0.88rem] text-[var(--text-3)] mt-5 text-center">
          {mode === 'signup' ? (
            <>Already have an account?{' '}
              <button className="text-[var(--accent)] hover:underline" onClick={() => { setMode('login'); setErr(''); }}>
                Sign in
              </button>
            </>
          ) : (
            <>New here?{' '}
              <button className="text-[var(--accent)] hover:underline" onClick={() => { setMode('signup'); setErr(''); }}>
                Create an account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
